import { create } from "zustand"
import { persist } from "zustand/middleware"

import type { NotificationView } from "./use-notifications"

export type NotificationFilterKey = "types" | "reasons" | "actorIds"

/** "From" value for notifications no person triggered; never a real user id. */
export const SYSTEM_ACTOR = "system"

/** The page's pills; archived rows from the old panel stay hidden. */
export type NotificationListView = "inbox" | "unread" | "mentions"

/** Pill order. The page opens on Unread every time; the last pill is All. */
export const NOTIFICATION_LIST_VIEWS: NotificationListView[] = [
	"unread",
	"mentions",
	"inbox",
]
export const DEFAULT_NOTIFICATION_VIEW: NotificationListView = "unread"

export interface NotificationFilters {
	types: string[]
	reasons: string[]
	actorIds: string[]
}

interface NotificationFilterState extends NotificationFilters {
	view: NotificationListView
	setView: (view: NotificationListView) => void
	toggle: (key: NotificationFilterKey, value: string) => void
	clear: (key: NotificationFilterKey) => void
}

export const useNotificationFilterState = create<NotificationFilterState>()(
	persist(
		(set) => ({
			types: [],
			reasons: [],
			actorIds: [],
			view: DEFAULT_NOTIFICATION_VIEW,
			setView: (view) => set({ view }),
			toggle: (key, value) =>
				set((prev) => ({
					[key]: prev[key].includes(value)
						? prev[key].filter((v) => v !== value)
						: [...prev[key], value],
				})),
			clear: (key) => set({ [key]: [] }),
		}),
		{
			name: "notification-filters",
			// Filters persist; the view does not, so a reload opens on Unread.
			partialize: ({ types, reasons, actorIds }) => ({
				types,
				reasons,
				actorIds,
			}),
			merge: (persisted, current) => mergePersistedFilters(persisted, current),
		},
	),
)

/** Older stored state carried a view; it is dropped so the page still opens on Unread. */
export function mergePersistedFilters<T extends NotificationFilters>(
	persisted: unknown,
	current: T,
): T {
	const stored = (persisted ?? {}) as Partial<NotificationFilters>
	return {
		...current,
		...(Array.isArray(stored.types) && { types: stored.types }),
		...(Array.isArray(stored.reasons) && { reasons: stored.reasons }),
		...(Array.isArray(stored.actorIds) && { actorIds: stored.actorIds }),
	}
}

export interface NotificationInboxArgs {
	view: NotificationView
	limit: number
	reasons?: string[]
	types?: string[]
	actorIds?: string[]
	systemActor?: boolean
}

/** A view persisted before Archived was removed falls back to All. */
export function listView(view: string): NotificationListView {
	return view === "unread" || view === "mentions" ? view : "inbox"
}

/** Empty filters are left out: the query treats a missing key as "any". */
export function notificationInboxArgs(
	state: NotificationFilters & { view: string },
	limit: number,
): NotificationInboxArgs {
	const people = state.actorIds.filter((id) => id !== SYSTEM_ACTOR)
	return {
		view: listView(state.view),
		limit,
		...(state.reasons.length > 0 && { reasons: state.reasons }),
		...(state.types.length > 0 && { types: state.types }),
		...(people.length > 0 && { actorIds: people }),
		...(state.actorIds.includes(SYSTEM_ACTOR) && { systemActor: true }),
	}
}

export function hasNotificationFilters(state: NotificationFilters) {
	return state.types.length + state.reasons.length + state.actorIds.length > 0
}
